import { Suspense, lazy } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";

// Lazy load all page components
const Login = lazy(() => import("@/components/screens/Login"));
const Dashboard = lazy(() => import("@/components/screens/Dashboard"));
const ServiceDashboard = lazy(() => import("@/components/screens/ServiceDashboard"));
const SalesDashboard = lazy(() => import("@/components/screens/SalesDashboard"));
const Profile = lazy(() => import("@/components/screens/Profile"));
const Customer = lazy(() => import("@/components/screens/Customer"));
const CustomerFormPage = lazy(() => import("@/components/screens/CustomerFormPage"));
const UsersApp = lazy(() => import("@/components/screens/UsersApp"));
const UserAppFormPage = lazy(() => import("@/components/screens/UserAppFormPage"));
const Platform = lazy(() => import("@/components/screens/Platform"));
const PlatformFormPage = lazy(() => import("@/components/screens/PlatformFormPage"));
const SalesProduct = lazy(() => import("@/components/screens/SalesProduct"));
const SalesProductFormPage = lazy(() => import("@/components/screens/SalesProductFormPage"));
const SalesProductPrice = lazy(() => import("@/components/screens/SalesProductPrice"));
const SalesProductPriceFormPage = lazy(() => import("@/components/screens/SalesProductPriceFormPage"));
const UsersBackoffice = lazy(() => import("@/components/screens/UsersBackoffice"));
const UserBackofficeFormPage = lazy(() => import("@/components/screens/UserBackofficeFormPage"));
const Workflow = lazy(() => import("@/components/screens/Workflow"));
const WorkflowFormPage = lazy(() => import("@/components/screens/WorkflowFormPage"));
const WorkflowView = lazy(() => import("@/components/screens/WorkflowView"));
const Prompts = lazy(() => import("@/components/screens/Prompts"));
const PromptFormPage = lazy(() => import("@/components/screens/PromptFormPage"));
const CoverageArea = lazy(() => import("@/components/screens/CoverageArea"));
const CoverageAreaFormPage = lazy(() => import("@/components/screens/CoverageAreaFormPage"));
const ChangelogLogs = lazy(() => import("@/components/screens/ChangelogLogs"));
const ChangelogTables = lazy(() => import("@/components/screens/ChangelogTables"));
const ChangelogTableFormPage = lazy(() => import("@/components/screens/ChangelogTableFormPage"));
const Plans = lazy(() => import("@/components/screens/Plans"));
const PlanFormPage = lazy(() => import("@/components/screens/PlanFormPage"));
const NotFound = lazy(() => import("@/components/screens/NotFound"));

const LoadingFallback = () => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      width: "100vw",
    }}
  >
    <CircularProgress color="primary" size={40} />
  </Box>
);

export default function AppRoutes() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<Navigate to="/dashboard/service/PT-BR" replace />} />

        {/* Login */}
        <Route path="/login/:lang" element={<Login />} />

        {/* Dashboard Routes */}
        <Route path="/dashboard/:lang" element={<Dashboard />} />
        <Route path="/dashboard/service/:lang" element={<ServiceDashboard />} />
        <Route path="/dashboard/sales/:lang" element={<SalesDashboard />} />
        <Route path="/dashboard/managerial/:lang" element={<Dashboard />} />

        {/* Profile Routes */}
        <Route path="/profile/:lang" element={<Profile selectedNavigationItem="profile" />} />
        <Route path="/profile/change-password/:lang" element={<Profile selectedNavigationItem="change-password" />} />
        <Route path="/profile/permissions/:lang" element={<Profile selectedNavigationItem="permissions" />} />

        {/* Customer Routes */}
        <Route path="/customers/accounts/:lang" element={<Customer />} />
        <Route path="/customers/accounts/create/:lang" element={<CustomerFormPage />} />
        <Route path="/customers/accounts/update/:customerId/:lang" element={<CustomerFormPage />} />

        {/* User App Routes */}
        <Route path="/customers/userApp/:lang" element={<UsersApp />} />
        <Route path="/customers/userApp/create/:lang" element={<UserAppFormPage />} />
        <Route path="/customers/userApp/update/:userId/:lang" element={<UserAppFormPage />} />
        <Route path="/customers/userApp/filterByCustomer/:customerId/:lang" element={<UsersApp />} />

        {/* Platform Routes */}
        <Route path="/platform/:lang" element={<Platform />} />
        <Route path="/platform/create/:lang" element={<PlatformFormPage />} />
        <Route path="/platform/update/:platformId/:lang" element={<PlatformFormPage />} />

        {/* Sales Product Routes */}
        <Route path="/salesProduct/:lang" element={<SalesProduct />} />
        <Route path="/salesProduct/create/:lang" element={<SalesProductFormPage />} />
        <Route path="/salesProduct/update/:salesProductId/:lang" element={<SalesProductFormPage />} />

        {/* Sales Product Price Routes */}
        <Route path="/salesProductPrice/create/:productId/:lang" element={<SalesProductPriceFormPage />} />
        <Route path="/salesProductPrice/filterByProductId/:productId/:lang" element={<SalesProductPrice />} />
        <Route path="/salesProductPrice/update/:productId/:lang" element={<SalesProductPriceFormPage />} />

        {/* Users Backoffice Routes */}
        <Route path="/usersBackoffice/:lang" element={<UsersBackoffice />} />
        <Route path="/usersBackoffice/create/:lang" element={<UserBackofficeFormPage />} />
        <Route path="/usersBackoffice/update/:userId/:lang" element={<UserBackofficeFormPage />} />

        {/* Workflow Routes */}
        <Route path="/workflow/:lang" element={<Workflow />} />
        <Route path="/workflow/create/:lang" element={<WorkflowFormPage />} />
        <Route path="/workflow/update/:workflowId/:lang" element={<WorkflowFormPage />} />
        <Route path="/workflow/flow/:workflowId/:lang" element={<WorkflowView />} />

        {/* Prompt Routes */}
        <Route path="/prompt/:lang" element={<Prompts />} />
        <Route path="/prompt/create/:lang" element={<PromptFormPage />} />
        <Route path="/prompt/update/:promptId/:lang" element={<PromptFormPage />} />

        {/* Coverage Area Routes */}
        <Route path="/coverage-area/:lang" element={<CoverageArea />} />
        <Route path="/coverage-area/create/:lang" element={<CoverageAreaFormPage />} />
        <Route path="/coverage-area/update/:coverageAreaId/:lang" element={<CoverageAreaFormPage />} />

        {/* Changelog Routes */}
        <Route path="/changelog/logs/:lang" element={<ChangelogLogs />} />
        <Route path="/changelog/tables/:lang" element={<ChangelogTables />} />
        <Route path="/changelog/tables/create/:lang" element={<ChangelogTableFormPage />} />
        <Route path="/changelog/tables/update/:tableId/:lang" element={<ChangelogTableFormPage />} />

        {/* Subscriptions (Assinaturas) Routes */}
        <Route path="/subscriptions/:lang" element={<Plans />} />
        <Route path="/subscriptions/create/:lang" element={<PlanFormPage />} />
        <Route path="/subscriptions/update/:subscriptionId/:lang" element={<PlanFormPage />} />

        {/* 404 Not Found */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}
